import { SyntheticEvent, useCallback, useMemo } from "react";
import { Some } from "space-monad";
import styled from "styled-components";
import { Image } from "../../../../components/image";
import { AddImage } from "../../../../components/image/add-image";
import {
  BORDER_SIZE,
  IMAGE_SIZE,
  PADDING_SIZE,
  useKycImage,
} from "../../../../components/image/common";
import {
  FileCategory,
  FileType,
  ServiceDetails,
} from "../../../../store/verification/models/internal";
import { VerificationService } from "../../../../store/verification/service";
import { CanEditKyc } from "./can-edit-kyc";

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    ${IMAGE_SIZE + PADDING_SIZE + BORDER_SIZE}px
  );
  grid-gap: 20px;
  flex-grow: 1;
`;

export function Images({
  documents,
  kycId,
  options,
  defaults,
}: {
  documents: ServiceDetails["documents"];
  kycId: string;
  options: {
    type: FileType;
    category: FileCategory;
  };
  defaults: {
    type: FileType["T"];
    category: FileCategory["T"];
  };
}) {
  return (
    <Grid>
      {documents.map((doc) => (
        <SingleImage {...doc} kycId={kycId} key={doc.id} />
      ))}
      <CanEditKyc>
        <AddImage kycId={kycId} options={options} defaults={defaults} />
      </CanEditKyc>
    </Grid>
  );
}

function SingleImage({
  id,
  kycId,
  fileName,
  ...props
}: ServiceDetails["documents"][number] & { kycId: string }) {
  const imageUrl = useMemo(() => Some({ docId: id, kycId }), [id, kycId]);

  const currentImage = useKycImage(imageUrl);

  const onDownload = useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation();
      VerificationService.downloadKycDocument({
        docId: id,
        kycId,
        fileName,
      });
    },
    [VerificationService.downloadKycDocument, id]
  );

  return currentImage.fold(
    () => null,
    (file) => (
      <Image
        file={file}
        id={id}
        fileName={fileName}
        onDownload={onDownload}
        {...props}
      />
    )
  );
}
